import { Button, Divider, Space, Spin, Typography } from 'antd'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { cancelDocument } from '~/states/modules/documentDetail'
import { ContainerTabs, DivContainer } from '../styles'
import styles from './index.module.scss'
import UpdateSignersFormModal from './updateSignersFormModal'
import PropTypes from 'prop-types'
import DOMPurify from 'dompurify'

const { Title, Paragraph } = Typography

// Deve ficar igual ao pattern usado em updateSignersFormModal.js — a Clicksign
// rejeita nomes de signatário que não sejam nome e sobrenome só com letras.
const SIGNER_NAME_PATTERN = /^[A-Za-zÀ-ÖØ-öø-ÿ']+(?:[-\s][A-Za-zÀ-ÖØ-öø-ÿ']+)+$/

/**
 * SignersTab component displays signing party information and actions for a document
 *
 * @param {Object[]} signingParties - Array of signing parties with their signers
 * @param {Function} handleEditSigners - Handler for editing signers infos - calls the document/{id}/signers endpoint
 * @param {boolean} loadingSign - Waitng for the backend to send the document for the signing platform
 * @param {boolean} sent - Whether document has been alredy sent for siging
 * @param {boolean} signed - Whether document has been signed
 * @param {Function} downloadButton - Handler for downloading siging certificate
 * @param {Function} handlesendForSigning - Handler for sending document to signing platform
 * @returns {JSX.Element|null} SignersTab component or null if there are no signing parties
 */
const SignersTab = ({
	signingParties,
	message,
	responsibleGroup,
	handleEditSigners,
	loadingSign,
	sent,
	signed,
	downloadButton,
	handlesendForSigning,
}) => {
	const dispatch = useDispatch()
	const cancelledDocument = useSelector(
		({ documentDetail }) => documentDetail.cancelledDocument
	)

	const documentId = useSelector((state) => state.documentDetail.data.id)

	/**
	 * Handles cancelling the document
	 */
	const cancelDocumentHandler = () => {
		dispatch(cancelDocument(documentId))
	}

	/**
	 * Memoized check if all signer information is complete and valid
	 *
	 * Validates that:
	 * - All signing parties have signers
	 * - All signers have their required fields filled out
	 * - Email fields contain valid email addresses
	 * - Name fields are not empty strings
	 *
	 * @returns {boolean} True if all signer info is complete and valid
	 */
	const signersInfoAreCompleted = React.useMemo(() => {
		return signingParties.every((signingParty) =>
			signingParty.signers.every((signer) =>
				signer.required === false ||
				signer.fields.every((field) => {
					if (field.type === 'email') {
						const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
						return field.valueVariable && emailRegex.test(field.valueVariable)
					} else if (field.type === 'name') {
						return (
							field.valueVariable &&
							SIGNER_NAME_PATTERN.test(field.valueVariable.trim())
						)
					}
					return field.valueVariable
				})
			)
		)
	}, [signingParties])

	const [showSignersFormModal, setShowSignersFormModal] = useState(false)

	// Only render if signers exists and has items
	if (!signingParties?.length) return null

	const sanitizedMessage = DOMPurify.sanitize(message)

	return (
		<>
			<UpdateSignersFormModal
				handleCancel={() => setShowSignersFormModal(false)}
				handleEditSigners={handleEditSigners}
				signingParties={signingParties}
				showModal={showSignersFormModal}
				message={sanitizedMessage}
				responsibleGroup={responsibleGroup}
			/>

			{message !== '' && (
				<ContainerTabs>
					<Title level={4} style={{ marginBottom: 10, fontSize: 18 }}>
						Mensagem
					</Title>
					<div
						style={{
							fontSize: 14,
							color: '#666',
							marginLeft: 20,
						}}
						dangerouslySetInnerHTML={{ __html: sanitizedMessage }}
					/>
					<Divider />
				</ContainerTabs>
			)}

			{responsibleGroup.length > 0 && (
				<ContainerTabs>
					<Title level={4} style={{ marginBottom: 10, fontSize: 18 }}>
						Responsáveis:
					</Title>
					{responsibleGroup.map((person, index) => (
						<div
							key={index}
							style={{ marginBottom: 15, padding: '0 0 0 10px' }}>
							<Paragraph
								style={{ marginBottom: 2, marginLeft: 10, fontSize: 12 }}>
								Nome:{' '}
								<strong style={{ color: '#646464' }}>{person.name}</strong>
							</Paragraph>
							<Paragraph
								style={{ marginBottom: 0, marginLeft: 10, fontSize: 12 }}>
								Email:{' '}
								<strong style={{ color: '#646464' }}>{person.email}</strong>
							</Paragraph>
						</div>
					))}
					<Divider />
				</ContainerTabs>
			)}

			{signingParties.map((signingParty, index) => (
				<ContainerTabs key={index}>
					{index > 0 && <Divider />}
					<Title level={4} style={{ marginBottom: 5, fontSize: 18 }}>
						{signingParty.title}
					</Title>
					{signingParty.signers.map((signer, signerIndex) => (
						<DivContainer key={signerIndex}>
							<div>
								<Title
									level={5}
									style={{ marginTop: 10, marginBottom: 0, fontSize: 14 }}>
									{signer.title}
								</Title>
								{signer.fields.map((field, index) => (
									<div key={index}>
										<Paragraph
											style={{
												color: '#000',
												fontSize: 12,
												marginLeft: 10,
												marginBottom: 2,
											}}>
											{field.value}:
											<strong
												style={{
													color: `${
														!field.valueVariable ? '#e6e6e6' : '#646464'
													}`,
													marginLeft: 5,
													fontSize: 12,
													display: 'inline',
												}}>
												{!field.valueVariable
													? 'Aguardando preenchimento dos dados'
													: field.valueVariable}
											</strong>
										</Paragraph>
									</div>
								))}

								{sent && (
									<div>
										<Paragraph style={{ color: '#000', fontSize: 12 }}>
											Status:
											<Paragraph
												style={{
													color: '#646464',
													fontSize: 12,
													display: 'inline',
													marginLeft: 5,
												}}>
												{signer.status}
											</Paragraph>
										</Paragraph>
									</div>
								)}
							</div>
						</DivContainer>
					))}
				</ContainerTabs>
			))}
			<div
				style={{
					display: 'flex',
					justifyContent: 'right',
					marginTop: 10,
				}}>
				<div>
					{!sent && (
						<Button
							key="editar"
							type="primary"
							className={styles.button}
							onClick={() => setShowSignersFormModal(true)}
							disabled={loadingSign}>
							Editar
						</Button>
					)}
					{!sent && signersInfoAreCompleted && (
						<Button key="assinar" type="primary" onClick={handlesendForSigning}>
							{loadingSign ? <Spin spinning={loadingSign} /> : 'Assinar'}
						</Button>
					)}
					{sent && (
						<Space>
							{!signed && (
								<Button
									key="cancelar"
									type="primary"
									danger
									loading={cancelledDocument.loading}
									onMouseDown={(e) => e.preventDefault()}
									onClick={cancelDocumentHandler}>
									Anular Documento
								</Button>
							)}
							<Button key="assinar" type="primary" onClick={downloadButton}>
								Download do Certificado
							</Button>
						</Space>
					)}
				</div>
			</div>
		</>
	)
}

SignersTab.propTypes = {
	signingParties: PropTypes.array.isRequired,
	message: PropTypes.string,
	responsibleGroup: PropTypes.array,
	handleEditSigners: PropTypes.func.isRequired,
	loadingSign: PropTypes.bool.isRequired,
	sent: PropTypes.bool.isRequired,
	signed: PropTypes.bool.isRequired,
	downloadButton: PropTypes.func.isRequired,
	handlesendForSigning: PropTypes.func.isRequired,
}

export default SignersTab
